import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, RefreshCw, CheckCircle } from "lucide-react";

// 🔗 API Endpoints
const API_GET_COOKIES = "https://backend.aktcorp.com/cookies";
const API_DELETE_COOKIE = (id) => `https://backend.aktcorp.com/cookies/${id}`;

// 📦 Pagination constant
const ITEMS_PER_PAGE = 20;

const ConsentTable = () => {
  const [cookies, setCookies] = useState([]); // 🍪 Store fetched cookies
  const [selectedIds, setSelectedIds] = useState([]); // ✅ Selected items for bulk delete
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const token = localStorage.getItem("authToken"); // 🔑 JWT Token from storage

  /**
   * 🔄 Fetch cookies from API (GET)
   */
  const fetchCookies = async () => {
    try {
      const response = await axios.get(API_GET_COOKIES, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data?.data || []; // <- Fix here

      setCookies(data);
    } catch (error) {
      console.error("❌ Error fetching cookies:", error);
      setCookies([]); // fallback to empty array
    } finally {
      setIsLoading(false);
    }
  };


  /**
   * 🗑️ Delete single cookie by ID (DELETE)
   */
  const deleteCookie = async (id) => {
    try {
      await axios.delete(API_DELETE_COOKIE(id), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCookies((prev) => prev.filter((item) => item.id !== id));
      setSuccessMessage("Deleted successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("❌ Error deleting cookie:", error);
    }
  };

  /**
   * 🗑️ Bulk delete selected cookies (DELETE)
   */
  const deleteSelectedCookies = async () => {
    try {
      for (let id of selectedIds) {
        await deleteCookie(id);
      }
      setSelectedIds([]);
    } catch (error) {
      console.error("❌ Error deleting selected cookies:", error);
    }
  };

  /**
   * 🔄 Refresh cookies list
   */
  const refreshCookies = async () => {
    setIsRefreshing(true);
    await fetchCookies();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  // 📅 Pagination Logic
  const totalPages = Math.ceil((cookies?.length || 0) / ITEMS_PER_PAGE);
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItems = cookies.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    fetchCookies();
  }, []);

  // 💻 UI Rendering
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-800">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 bg-gray-800 text-white rounded-lg">
      {/* 🔄 Refresh Button */}
      <div className="flex justify-end mb-4">
        <button
          className={`rounded-full ${isRefreshing ? "animate-spin" : ""}`}
          onClick={refreshCookies}
        >
          <RefreshCw size={22} />
        </button>
      </div>

      {/* ✅ Bulk Delete */}
      {selectedIds.length > 0 && (
        <button
          onClick={deleteSelectedCookies}
          className="bg-red-600 text-white px-3 py-1 rounded mb-4"
        >
          Delete Selected ({selectedIds.length})
        </button>
      )}

      {/* 🎉 Success Message */}
      {successMessage && (
        <div className="mb-4 text-green-400 flex items-center gap-2">
          <CheckCircle size={20} />
          {successMessage}
        </div>
      )}

      {/* 📋 Table */}
      <div className="overflow-x-auto rounded-lg">
        <table className="min-w-[900px] w-full text-sm">
          <thead className="bg-gray-700">
            <tr>
              <th className="p-2 text-center">
                <input
                  type="checkbox"
                  checked={selectedIds.length === (cookies?.length || 0)}
                  onChange={() =>
                    setSelectedIds(
                      selectedIds.length === cookies.length
                        ? []
                        : cookies.map((item) => item.id)
                    )
                  }
                />
              </th>
              <th className="p-2 text-left">Consent</th>
              <th className="p-2 text-left">IP Address</th>
              <th className="p-2 text-left">Device</th>
              <th className="p-2 text-left">Country</th>
              <th className="p-2 text-left">City</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((item) => (
              <tr key={item.id} className="hover:bg-gray-700">
                <td className="p-2 text-center">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={() =>
                      setSelectedIds((prev) =>
                        prev.includes(item.id)
                          ? prev.filter((id) => id !== item.id)
                          : [...prev, item.id]
                      )
                    }
                  />
                </td>
                <td className="p-2">{item.consent}</td>
                <td className="p-2">{item.ipAddress}</td>
                <td className="p-2">{item.device}</td>
                <td className="p-2">{item.country}</td>
                <td className="p-2">{item.city}</td>
                <td className="p-2">
                  {new Date(item.visitTime).toISOString().slice(0, 10)}
                </td>
                <td className="p-2 text-center">
                  <button
                    onClick={() => deleteCookie(item.id)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 📅 Pagination */}
      <div className="flex justify-center mt-4">
        {Array.from({ length: totalPages }, (_, index) => (
          <button
            key={index}
            className={`mx-1 px-2 py-1 rounded ${currentPage === index + 1 ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300"
              }`}
            onClick={() => setCurrentPage(index + 1)}
          >
            {index + 1}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ConsentTable;
